import { CommonChartProps, CommonHCChartExtended } from "types";
import { ThemeFonts, ThemeHexColors } from "@civicscience/chops";
import {
  getHCMenuItems,
  getHCSubTitleConfig,
  getHCTitleConfig,
  hcChartsAnimationDuration,
  hcChartsExportScale,
  hcChartsLegendXDefault,
  hcChartsLegendYDefault,
  hcChartsLegendYSmallContainerOffsetDefault,
  hcChartsResponsiveMediumMaxWidth,
  hcChartsResponsiveMobileMaxWidth,
  renderHCAreas,
  renderHCLeftBottomArea,
} from "utils/charts/hcChartUtils";

import { ResponsiveBreakPoints } from "consts";
import { pickElementsByTargetSize } from "utils/base";

type getTimeViewHCChartSharedOptionsProps = CommonChartProps & {
  chartYSpacing?: number;
  chartXSpacing?: number;
  chartLegendYSpacing?: number;
  chartLegendY?: number;
  chartLegendX?: number;
  chartLegendExportY?: number;
  chartLegendExportHeight?: number;
  chartXMargin?: number;
  chartMarginTop?: number;
  chartMarginBottom?: number;
  chartExportMarginTop?: number;
  chartExportMarginBottom?: number;
  chartSubTitleHeight?: number;
  chartTitleGap?: number;
  chartHeaderHeight?: number;
  chartNonExportHeight?: number;
  chartExportHeight?: number;
  chartExportWidth?: number;
  hasTitle?: boolean;
  hasSubTitle?: boolean;
  hasBottomLeft?: boolean;
  viewAsLine?: boolean;
  xAxisLabelFormatter?: (this: Highcharts.AxisLabelsFormatterContextObject) => string;
  xAxisTickPositions?: number[];
};

/**
 * Formatter intended to be used when the Timeview represents quarters.
 * Converts the label values to a have a quarter and a year: i.e. "Q1/21"
 */
export function labelFormatterQuarters(this: Highcharts.AxisLabelsFormatterContextObject): string {
  const d = new Date(this.value);
  const month = d.getMonth() + 1;
  const year = d.getFullYear().toString().slice(-2);
  return `Q${Math.ceil(month / 3)}/${year}`;
}

/**
 * Returns the "target" number of x axis labels based on the window size.
 * We won't exceed this number of labels, but we may have fewer.
 * This approach works well for the quarter labels because the size of the text is relatively consistent.
 */
const getQuarterLabelCountByWindowSize = (width: number) => {
  if (width < ResponsiveBreakPoints.TabletLg[0]) {
    return 10;
  }

  if (width < ResponsiveBreakPoints.Notebook[0]) {
    return 13;
  }

  if (width < ResponsiveBreakPoints.NotebookLg[0]) {
    return 15;
  }

  return 20;
};

/**
 * Tick positions intended to be used when the Timeview represents quarters.
 * A few things we want to accomplish:
 *
 * 1. Ensure that the only values we use as ticks comes from the dates array.
 *    We don't want HC selecting dates "in between" quarters as a label.
 * 2. Ensure that the first date is always included (but not necessarily the last).
 * 3. Ensure that the dates are evenly spaced based on the total number of dates.
 * 4. Don't exceed a certain number of labels based on the window size.
 */
export const tickPositionsQuarters = (dates: Date[]): number[] => {
  const datesAsTimestamps = dates.map((date) => new Date(date).getTime());

  return pickElementsByTargetSize(datesAsTimestamps, getQuarterLabelCountByWindowSize(window.innerWidth));
};

export const getTimeViewHCChartSharedOptions = ({
  showTitle = false,
  showSubTitle = false,
  chartTitle,
  hasTitle = false,
  chartSubTitle,
  hasSubTitle = false,
  exportFilename,
  showExport = true,
  chartYSpacing = 0,
  chartXSpacing = 0,
  chartLegendY = hcChartsLegendYDefault,
  chartLegendX = hcChartsLegendXDefault,
  chartLegendExportY,
  chartExportMarginTop = 0,
  chartMarginTop = 0,
  chartMarginBottom = 0,
  chartExportMarginBottom = 0,
  chartSubTitleHeight = 0,
  chartTitleGap = 0,
  chartHeaderHeight = 0,
  chartExportHeight = 0,
  chartExportWidth = 0,
  chartNonExportHeight = 0,
  labelBottomLeft,
  hasBottomLeft = false,
  showLabelBottomLeft = false,
  chartColors = [],
  viewAsLine = true,
  xAxisLabelFormatter,
  xAxisTickPositions,
}: getTimeViewHCChartSharedOptionsProps) => {
  const timeViewMarginBottomOffset = 10;

  return {
    chart: {
      type: viewAsLine ? "line" : "area",
      zoomType: "x",
      backgroundColor: ThemeHexColors.Light,
      colorCount: chartColors.length || 0,
      style: {
        fontFamily: ThemeFonts.default,
        fontSize: "12px",
      },
      spacing: [chartYSpacing, chartXSpacing, showLabelBottomLeft ? chartYSpacing : 0, chartXSpacing],
      marginTop: chartMarginTop,
      marginBottom: chartMarginBottom + timeViewMarginBottomOffset,
      height: chartNonExportHeight,
      events: {
        render(this: CommonHCChartExtended) {
          showLabelBottomLeft && hasBottomLeft && renderHCLeftBottomArea({ chart: this, labelBottomLeft });
        },
      },
    },

    exporting: {
      allowHTML: true,
      filename: exportFilename,
      scale: hcChartsExportScale,
      showTable: false,
      enabled: showExport,
      sourceWidth: chartExportWidth,
      sourceHeight: undefined,
      buttons: {
        contextButton: {
          menuItems: getHCMenuItems(),
        },
      },
      chartOptions: {
        chart: {
          spacing: [chartYSpacing, chartXSpacing, chartYSpacing, chartXSpacing],
          marginTop: chartExportMarginTop,
          marginBottom: chartExportMarginBottom,
          height: chartExportHeight,
          events: {
            render(this: CommonHCChartExtended) {
              return renderHCAreas({
                chart: this,
                labelBottomLeft,
                showLabelBottomLeft: true,
              });
            },
          },
        },
        legend: {
          enabled: true,
          shadow: false,
          y: chartLegendExportY,
        },
        title: getHCTitleConfig({
          text: chartTitle,
          enabled: hasTitle,
        }),
        subtitle: getHCSubTitleConfig({
          text: chartSubTitle,
          enabled: hasSubTitle,
          y: chartHeaderHeight - chartSubTitleHeight - chartTitleGap,
        }),
      },
    },

    title: getHCTitleConfig({
      text: chartTitle,
      enabled: showTitle,
    }),
    subtitle: getHCSubTitleConfig({
      text: chartSubTitle,
      enabled: showSubTitle,
      y: chartHeaderHeight - chartSubTitleHeight - chartTitleGap,
    }),

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: hcChartsResponsiveMediumMaxWidth,
          },
          chartOptions: {
            chart: {
              height: chartNonExportHeight - hcChartsLegendYSmallContainerOffsetDefault,
              marginBottom: chartMarginBottom - hcChartsLegendYSmallContainerOffsetDefault * 2,
            },
            legend: {
              x: chartLegendX,
              y: chartLegendY - hcChartsLegendYSmallContainerOffsetDefault / 2,
            },
          },
        },
        {
          condition: {
            maxWidth: hcChartsResponsiveMobileMaxWidth + 100,
          },
          chartOptions: {
            chart: {
              height: chartNonExportHeight - hcChartsLegendYSmallContainerOffsetDefault * 2,
              marginBottom: chartMarginBottom - hcChartsLegendYSmallContainerOffsetDefault * 2,
            },
            legend: {
              x: chartLegendX,
              y: chartLegendY,
            },
          },
        },
        {
          condition: {
            maxWidth: hcChartsResponsiveMobileMaxWidth,
          },
          chartOptions: {
            chart: {
              style: {
                fontFamily: ThemeFonts.default,
                fontSize: "10px",
              },
              legend: {
                x: chartLegendX,
                y: chartLegendY - hcChartsLegendYSmallContainerOffsetDefault * 3,
              },
            },
          },
        },
      ],
    },

    xAxis: {
      type: "datetime",
      title: {
        text: "Date",
      },
      tickWidth: 0.5,
      gridLineWidth: 0,
      labels: {
        formatter: xAxisLabelFormatter,
      },
      tickPositions: xAxisTickPositions,
    },

    plotOptions: {
      series: {
        cursor: "pointer",
        className: "popup-on-click",
        lineWidth: 2,
        animation: {
          duration: hcChartsAnimationDuration,
        },
      },
      area: {
        fillOpacity: 0,
        marker: {
          radius: 5,
        },
        lineWidth: 2,
        states: {
          hover: {
            lineWidth: 1,
          },
        },
        threshold: null,
      },
    },
  };
};
